import React, { useState } from "react";
import { Edit } from "@mui/icons-material";
import { Alert, AlertTitle, Box, Button, FormLabel, Switch, Typography } from "@mui/material";

import { Progress } from "ui";

import { useCustomWorkflow } from "fond/hooks/useCustomWorkflow";
import { WORKFLOW_KEYS } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { StackedNavigationButton, useStackedNavigationContext } from "fond/widgets";

const stageConfig: Record<string, { label: string }> = {
  base_data: { label: "Processing base data" },
  demand: { label: "Generating demand" },
  ug_gen: { label: "Generating underground network" },
  leadins: { label: "Generating leadins" },
  output: { label: "Preparing output" },
};

interface DataPrepPanelProps {
  isReadOnly?: boolean;
  canRun: boolean;
}

const DataPrepPanel: React.FC<DataPrepPanelProps> = ({ isReadOnly, canRun }) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const { open } = useStackedNavigationContext();
  const { workflowState, stages, runWorkflow, cancelWorkflow } = useCustomWorkflow({
    versionId,
    key: WORKFLOW_KEYS.MU_POWER.DATA_PREP,
  });
  const [serviceRoads, setServiceRoads] = useState<boolean>(false);

  const isInProgress = workflowState === "STARTING" || workflowState === "IN_PROGRESS";

  const handleRunDataPrep = () => {
    runWorkflow?.({ ServiceRoads: serviceRoads });
  };

  const handleCancelDataPrep = () => {
    cancelWorkflow?.();
  };

  const handleModifyData = () => {
    open("modification-muPowerBaseData");
  };

  return (
    <>
      {!canRun && workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Upload required layers</AlertTitle>
          Please upload all input layers before running data preparation.
        </Alert>
      )}

      {canRun && workflowState === undefined && (
        <Alert severity="info" variant="compact" sx={{ mb: 1 }}>
          Data is ready for preparation.
        </Alert>
      )}

      {workflowState === "ERROR" && (
        <Alert severity="error" variant="compact" sx={{ mb: 1 }}>
          <AlertTitle>Data preparation failed</AlertTitle>
          There was an error running data preparation. Please try again.
        </Alert>
      )}

      {workflowState === "COMPLETE" && (
        <Alert severity="success" variant="compact" sx={{ mb: 1 }}>
          Data preparation complete.
        </Alert>
      )}

      {stages && (isInProgress || workflowState === "ERROR") && (
        <Progress steps={stages.map(({ key, status }) => ({ ID: key, Label: stageConfig[key]?.label ?? key, Status: status }))} />
      )}

      {!isInProgress && (
        <Box display="flex" flexDirection="column">
          <Box width="100%" display="flex" flexDirection="row" alignItems="center" justifyContent="space-between">
            <FormLabel htmlFor="serviceRoads">Include Service Roads</FormLabel>
            <Switch
              id="serviceRoads"
              name="serviceRoads"
              checked={serviceRoads}
              onChange={(event) => setServiceRoads(event.target.checked)}
              disabled={isReadOnly || !canRun}
              data-testid="serviceRoads"
            />
          </Box>
          <Typography variant="caption" color="textSecondary">
            Service roads represent parking aisles, driveways, alleys, and similar routes.
          </Typography>
          <Button
            fullWidth
            variant="contained"
            size="small"
            sx={{ my: 1 }}
            disabled={!runWorkflow || !canRun || isReadOnly}
            onClick={handleRunDataPrep}
          >
            {workflowState === "COMPLETE" ? "Re-run data preparation" : "Run data preparation"}
          </Button>
        </Box>
      )}

      {isInProgress && (
        <Button fullWidth variant="contained" size="small" sx={{ my: 1 }} disabled={!cancelWorkflow || isReadOnly} onClick={handleCancelDataPrep}>
          Cancel
        </Button>
      )}

      {workflowState === "COMPLETE" && (
        <StackedNavigationButton icon={<Edit />} title="Modify data" onClick={handleModifyData} sx={{ m: 0, mt: 1 }} disabled={isReadOnly} />
      )}
    </>
  );
};

export default DataPrepPanel;
